import { type ReactNode } from 'react';
import { ContentWrapper } from '../components/ContentWrapper';
import { Details } from '../components/Details';
import { ContentNotFoundState } from '../components/DoqDisplays';
import { getDetailsData } from '../utils';
import { difference, intersection } from 'lodash';
import { PermissionContext } from '@ardoq/access-control';
import {
  APIComponentAttributes,
  ArdoqId,
  ViewpointDetailEntry,
} from '@ardoq/api-types';
import { type ComponentSurveyWithOptionalApprovalStatus } from '../components/SurveyButtons';
import { EnhancedScopeData } from '@ardoq/data-model';
import { Description } from '../components/Description';
import { Header3 } from '@ardoq/typography';
import { WorkspaceAndComponentHierarchy } from '../widgets/TopBar';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import {
  ExternalDocumentWidget,
  type ExternalDocumentsWidgetLabels,
} from '../widgets/ExternalDocumentsWidget';
import { Tag } from '@ardoq/status-ui';
import { SurveysDropdown } from '../components/SurveysDropdown';
import { LinkButton } from '@ardoq/button';
import { type OpenEditPriorityFieldsProps } from '../types';

export type DetailsDrawerLabels = {
  notFoundContentType?: string;
  notFoundTitle?: string;
  notFoundMessage?: ReactNode;
  descriptionTitle?: string;
  priorityFieldsTitle?: string;
  customFieldsTitle?: string;
  defaultFieldsTitle?: string;
  changePriorityFieldsLabel?: string;
  submitUpdateLabel?: string;
  changeRequestTooltip?: string;
  externalDocumentsLabels?: ExternalDocumentsWidgetLabels;
};

type DetailsDrawerProps = {
  scopeData?: EnhancedScopeData;
  component?: APIComponentAttributes;
  detailEntries?: ViewpointDetailEntry[];
  surveys?: ComponentSurveyWithOptionalApprovalStatus[] | null;
  onSurveyClick?: (surveyId: ArdoqId) => void;
  permissionContext: PermissionContext;
  priorityFieldNames?: string[];
  noPriorityFieldsBanner?: ReactNode;
  openEditPriorityFieldsProps?: OpenEditPriorityFieldsProps;
  labels?: DetailsDrawerLabels;
};

const DetailsDrawer = ({
  scopeData,
  component,
  detailEntries,
  surveys,
  onSurveyClick,
  permissionContext,
  priorityFieldNames = [],
  noPriorityFieldsBanner,
  openEditPriorityFieldsProps,
  labels,
}: DetailsDrawerProps) => {
  if (!scopeData || !component) {
    return (
      <ContentNotFoundState
        contentType={labels?.notFoundContentType ?? 'details'}
        title={labels?.notFoundTitle}
        message={labels?.notFoundMessage}
      />
    );
  }
  const { detailNames: customFieldNames, enhancedScopeData } = getDetailsData({
    scopeData,
    entity: component,
    entityTypeName: component.type,
    detailEntries,
    fieldFilter: 'custom-only',
  });
  const { detailNames: defaultFieldNames } = getDetailsData({
    scopeData,
    entity: component,
    entityTypeName: component.type,
    detailEntries,
    fieldFilter: 'default-only',
  });
  const shouldShowSurveysDropdown =
    surveys && surveys.length > 0 && onSurveyClick;

  const customFieldNamesWithoutPriorityFields = difference(
    customFieldNames,
    priorityFieldNames
  );

  const visiblePriorityFieldNames = intersection(
    priorityFieldNames,
    customFieldNames
  );

  return (
    <>
      <FlexBox
        justify="space-between"
        align="center"
        paddingX="large"
        paddingTop="small"
      >
        <Stack gap="small">
          <WorkspaceAndComponentHierarchy
            component={component}
            scopeData={scopeData}
          />
          <Box>
            <Tag>{component.type}</Tag>
          </Box>
        </Stack>
        {shouldShowSurveysDropdown && (
          <SurveysDropdown
            component={component}
            surveys={surveys}
            onSurveyClick={onSurveyClick}
            submitUpdateLabel={labels?.submitUpdateLabel}
            changeRequestTooltip={labels?.changeRequestTooltip}
          />
        )}
      </FlexBox>
      {component.description ? (
        <ContentWrapper>
          <Description component={component} title={labels?.descriptionTitle} />
        </ContentWrapper>
      ) : null}
      {visiblePriorityFieldNames.length > 0 && (
        <ContentWrapper>
          <FlexBox justify="space-between" align="center">
            <Header3>
              {labels?.priorityFieldsTitle ?? 'Priority fields'}
            </Header3>
            {openEditPriorityFieldsProps?.shouldShow && (
              <LinkButton {...openEditPriorityFieldsProps.linkProps}>
                {labels?.changePriorityFieldsLabel ?? 'Change priority fields'}
              </LinkButton>
            )}
          </FlexBox>
          <Details
            component={component}
            detailNames={visiblePriorityFieldNames}
            enhancedScopeData={enhancedScopeData}
            permissionContext={permissionContext}
          />
        </ContentWrapper>
      )}
      {noPriorityFieldsBanner && (
        <ContentWrapper>{noPriorityFieldsBanner}</ContentWrapper>
      )}
      <ContentWrapper>
        <Header3>{labels?.customFieldsTitle ?? 'Custom fields'}</Header3>
        <Details
          component={component}
          detailNames={customFieldNamesWithoutPriorityFields}
          enhancedScopeData={enhancedScopeData}
          permissionContext={permissionContext}
        />
      </ContentWrapper>
      <ContentWrapper>
        <Header3>{labels?.defaultFieldsTitle ?? 'Default fields'}</Header3>
        <Details
          component={component}
          detailNames={defaultFieldNames}
          enhancedScopeData={enhancedScopeData}
          permissionContext={permissionContext}
        />
      </ContentWrapper>
      <ExternalDocumentWidget
        externalDocuments={component.ardoq.externalDocuments ?? []}
        labels={labels?.externalDocumentsLabels}
      />
    </>
  );
};

export { DetailsDrawer };
